import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../utils/usePermissions';
import ProduitDetail from './ProduitDetail';
import BonCommandeCreate from './BonCommandeCreate';
import * as XLSX from 'xlsx';
import './ProduitsManagement.css';

const API_URL = import.meta.env.VITE_API_URL;

const hasValidDataSheet = (dataSheetPath) => {
    if (typeof dataSheetPath !== 'string') return false;
    const normalizedPath = dataSheetPath.trim();
    if (!normalizedPath) return false;

    const loweredPath = normalizedPath.toLowerCase();
    return loweredPath !== 'null' && loweredPath !== 'undefined';
};

const normalizeDecoupeForDisplay = (decoupe) => {
    const nombrePieces = Math.max(1, parseInt(decoupe && decoupe.nombrePieces, 10) || 1);
    const longueurPieceMetre = Math.max(0.001, Number(decoupe && decoupe.longueurPieceMetre) || 0.001);
    const metres = Number(decoupe && decoupe.metres) || Number((nombrePieces * longueurPieceMetre).toFixed(3));
    return { nombrePieces, longueurPieceMetre, metres };
};

const formatQuantiteAffichage = (produit) => {
    if (!produit) return '0';
    if (produit.modeGestionStock !== 'lineaire') {
        return `${Math.max(0, (Number(produit.quantite) || 0) - (Number(produit.quantiteReservee) || 0))}`;
    }

    const decoupes = Array.isArray(produit.decoupesLineaires)
        ? produit.decoupesLineaires.map(normalizeDecoupeForDisplay).filter(d => d.metres > 0)
        : [];

    if (decoupes.length === 1) {
        const { nombrePieces, longueurPieceMetre } = decoupes[0];
        return `${nombrePieces} piece${nombrePieces > 1 ? 's' : ''} x ${longueurPieceMetre} m`;
    }

    if (decoupes.length > 1) {
        return decoupes
            .map((decoupe) => `${decoupe.nombrePieces} piece${decoupe.nombrePieces > 1 ? 's' : ''} x ${decoupe.longueurPieceMetre} m`)
            .join(' + ');
    }

    return `${Number(produit.quantite) || 0} m`;
};

const getStockCounters = (produit) => {
    const physique = Math.max(0, Number(produit?.quantite) || 0);
    const reserve = Math.max(0, Number(produit?.quantiteReservee) || 0);
    const vendu = Math.max(0, Number(produit?.quantiteVendue) || 0);
    return {
        disponible: Math.max(0, physique - reserve),
        reserve,
        vendu
    };
};

const emptyProduitForm = {
    nomProduit: '',
    description: '',
    marque: '',
    typeProduit: '',
    categorie: '',
    quantite: 0,
    modeGestionStock: 'unite',
    prix: 0,
    prixAchat: 0,
    prixMetre: 0,
    TVA: 19,
    tauxGain: 0,
    imageProduit: null,
    dataSheet: null
};

const ProduitsManagement = ({ onNavigate, onLogout, currentPage }) => {
    const {hasPermission} = usePermissions();
    const produitFormRef = useRef(null);
    const [activeTab, setActiveTab] = useState('produits'); // 'produits', 'categories', 'types', 'triangles'
    const [viewingProduitId, setViewingProduitId] = useState(null);
    const [viewMode, setViewMode] = useState('list'); // 'list' (défaut) ou 'grid'
    const [sortConfig, setSortConfig] = useState({ key: 'nomProduit', direction: 'asc' });

    // États pour produits
    const [produits, setProduits] = useState([]);
    const [types, setTypes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [partenaires, setPartenaires] = useState([]);
    const [fournisseurs, setFournisseurs] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showProduitForm, setShowProduitForm] = useState(false);
    const [editingProduit, setEditingProduit] = useState(null);
    const [deletingDataSheet, setDeletingDataSheet] = useState(false);

    // États pour types
    const [showTypeForm, setShowTypeForm] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [typeLabel, setTypeLabel] = useState('');

    // États pour catégories
    const [showCategoryForm, setShowCategoryForm] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [categoryLabel, setCategoryLabel] = useState('');
    const [categoryType, setCategoryType] = useState('');

    // Triangles
    const [triangles, setTriangles] = useState([]);
    const [showTriangleForm, setShowTriangleForm] = useState(false);
    const [editingTriangle, setEditingTriangle] = useState(null);
    const [triangleForm, setTriangleForm] = useState({
        nom: '', description: '', categorie: '', statut: 'actif',
        produits: [], // [{ produitId, quantiteRecommandee }]
        imageFile: null
    });
    const [triangleFilterCat, setTriangleFilterCat] = useState('');
    const [triangleSearch, setTriangleSearch] = useState('');

    // États du Bon de commande depuis produits
    const [viewingBc, setViewingBc] = useState(false);
    const [bcPrefill, setBcPrefill] = useState(null);

    // États pour Déclaration de Mouvement
    const [showMouvementModal, setShowMouvementModal] = useState(false);
    const [mouvementProduit, setMouvementProduit] = useState(null);
    const [mouvementForm, setMouvementForm] = useState({ typeMouvement: 'retour_client', quantite: '', motif: '', clientId: '' });

    // Filtres
    const [searchTerm, setSearchTerm] = useState('');
    const [filterFournisseur, setFilterFournisseur] = useState('');
    const [sortBy, setSortBy] = useState('');

    const [produitForm, setProduitForm] = useState(emptyProduitForm);
    const editingDataSheetPath = editingProduit && hasValidDataSheet(editingProduit.dataSheet)
        ? editingProduit.dataSheet.trim()
        : '';

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('bc') === '1') {
            setViewingBc(true);
            setBcPrefill({
                fournisseurId: params.get('fournisseurId') || '',
                produitId: params.get('produitId') || ''
            });
        }
        fetchData();
    }, []);

    useEffect(() => {
        if (!showProduitForm || activeTab !== 'produits') return;

        const rafId = window.requestAnimationFrame(() => {
            produitFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });

        return () => window.cancelAnimationFrame(rafId);
    }, [showProduitForm, editingProduit, activeTab]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [prodRes, typeRes, partRes, fournRes, catRes, triRes, cliRes] = await Promise.all([
                axios.get(`${API_URL}/produits`, { headers }),
                axios.get(`${API_URL}/types-produit`, { headers }),
                axios.get(`${API_URL}/partners`, { headers }),
                axios.get(`${API_URL}/fournisseurs`, { headers }),
                axios.get(`${API_URL}/categories`, { headers }),
                axios.get(`${API_URL}/triangles`, { headers, params: { limit: 200 } }),
                axios.get(`${API_URL}/clients`, { headers, params: { limit: 1000 } })
            ]);

            setProduits(Array.isArray(prodRes.data) ? prodRes.data : (prodRes.data?.data || []));
            setTypes(Array.isArray(typeRes.data) ? typeRes.data : (typeRes.data?.data || []));
            setPartenaires(Array.isArray(partRes.data) ? partRes.data : (partRes.data?.data || []));
            setFournisseurs(Array.isArray(fournRes.data) ? fournRes.data : (fournRes.data?.data || []));
            setCategories(Array.isArray(catRes.data) ? catRes.data : (catRes.data?.data || []));
            setTriangles(triRes.data?.triangles || []);
            const clientsList = Array.isArray(cliRes.data)
                ? cliRes.data
                : (cliRes.data?.data || cliRes.data?.clients || []);
            setClients(clientsList);
        } catch (error) {
            console.error('Erreur fetchData:', error);
            Swal.fire('Erreur', 'Impossible de charger les données', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleProduitInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'modeGestionStock' && value === 'unite') {
            setProduitForm({ ...produitForm, modeGestionStock: value, prixMetre: 0 });
            return;
        }

        setProduitForm({ ...produitForm, [name]: value });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                Swal.fire('Erreur', 'Format d\'image non supporté. Seuls jpeg, jpg, png, gif, webp sont autorisés.', 'error');
                e.target.value = null;
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                Swal.fire('Erreur', 'La taille de l\'image dépasse la limite de 5MB.', 'error');
                e.target.value = null;
                return;
            }
            setProduitForm({ ...produitForm, imageProduit: file });
        }
    };

    const handleDataSheetChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                Swal.fire('Erreur', 'Seuls les fichiers PDF sont acceptés pour la fiche technique.', 'error');
                e.target.value = null;
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                Swal.fire('Erreur', 'La taille de la fiche technique dépasse la limite de 10MB.', 'error');
                e.target.value = null;
                return;
            }
            setProduitForm({ ...produitForm, dataSheet: file });
        }
    };

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const handleSubmitProduit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        const formData = new FormData();
        Object.keys(produitForm).forEach(key => {
            if (produitForm[key] !== null) {
                formData.append(key, produitForm[key]);
            }
        });

        try {
            if (editingProduit) {
                await axios.put(`${API_URL}/produits/${editingProduit._id}`, formData, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                });
                Swal.fire('Succès', 'Produit mis à jour', 'success');
            } else {
                await axios.post(`${API_URL}/produits`, formData, {
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                });
                Swal.fire('Succès', 'Produit ajouté', 'success');
            }
            setShowProduitForm(false);
            setEditingProduit(null);
            fetchData();
        } catch (error) {
            const data = error.response?.data;
            let msg = data?.message || 'Erreur lors de l\'enregistrement';
            if (data?.errors && Array.isArray(data.errors)) {
                msg += '\n' + data.errors.join('\n');
            }
            Swal.fire('Erreur', msg, 'error');
        }
    };

    const handleEditProduit = (produit) => {
        setEditingProduit(produit);
        setProduitForm({
            nomProduit: produit.nomProduit,
            description: produit.description,
            marque: produit.marque?._id || '',
            typeProduit: produit.typeProduit?._id || '',
            categorie: produit.categorie?._id || produit.categorie || '',
            quantite: produit.quantite || 0,
            modeGestionStock: produit.modeGestionStock || 'unite',
            prix: produit.prix || 0,
            prixAchat: produit.prixAchat || 0,
            prixMetre: produit.prixMetre || 0,
            TVA: produit.TVA || 19,
            tauxGain: produit.tauxGain || 0,
            imageProduit: null,
            dataSheet: null
        });
        setShowProduitForm(true);
    };

    const handleDuplicateProduit = (produit) => {
        setEditingProduit(null);
        setProduitForm({
            nomProduit: produit.nomProduit,
            description: produit.description,
            marque: produit.marque?._id || '',
            typeProduit: produit.typeProduit?._id || '',
            categorie: produit.categorie?._id || produit.categorie || '',
            quantite: produit.quantite || 0,
            modeGestionStock: produit.modeGestionStock || 'unite',
            prix: produit.prix || 0,
            prixAchat: produit.prixAchat || 0,
            prixMetre: produit.prixMetre || 0,
            TVA: produit.TVA || 19,
            tauxGain: produit.tauxGain || 0,
            imageProduit: null,
            dataSheet: null
        });
        setShowProduitForm(true);
    };

    const handleDeleteProduit = async (id) => {
        const result = await Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Cette action est irréversible !",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, supprimer !'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`${API_URL}/produits/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Supprimé !', 'Le produit a été supprimé.', 'success');
                fetchData();
            } catch (error) {
                Swal.fire('Erreur', 'Impossible de supprimer le produit', 'error');
            }
        }
    };

    const handleDeleteCurrentDataSheet = async () => {
        if (!editingProduit || !editingProduit._id) return;

        const result = await Swal.fire({
            title: 'Supprimer la fiche technique ?',
            text: 'Cette action supprimera aussi le fichier dans le dossier uploads.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, supprimer'
        });

        if (!result.isConfirmed) return;

        try {
            setDeletingDataSheet(true);
            const token = localStorage.getItem('token');

            await axios.delete(`${API_URL}/produits/${editingProduit._id}/datasheet`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setEditingProduit((prev) => {
                if (!prev) return prev;
                return { ...prev, dataSheet: null };
            });
            setProduitForm((prev) => ({ ...prev, dataSheet: null }));

            Swal.fire('Supprimée', 'La fiche technique a été supprimée.', 'success');
            fetchData();
        } catch (error) {
            Swal.fire('Erreur', error.response?.data?.message || 'Impossible de supprimer la fiche technique', 'error');
        } finally {
            setDeletingDataSheet(false);
        }
    };

    const handleSubmitType = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        try {
            if (editingType) {
                await axios.put(`${API_URL}/types-produit/${editingType._id}`, { label: typeLabel }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Succès', 'Type mis à jour', 'success');
            } else {
                await axios.post(`${API_URL}/types-produit`, { label: typeLabel }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Succès', 'Type ajouté', 'success');
            }
            setShowTypeForm(false);
            setEditingType(null);
            setTypeLabel('');
            fetchData();
        } catch (error) {
            Swal.fire('Erreur', 'Impossible d\'enregistrer le type', 'error');
        }
    };

    const handleSubmitCategory = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('token');
        try {
            if (editingCategory) {
                await axios.put(`${API_URL}/categories/${editingCategory._id}`, { label: categoryLabel }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Succès', 'Catégorie mise à jour', 'success');
            } else {
                await axios.post(`${API_URL}/categories`, { label: categoryLabel }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Succès', 'Catégorie ajoutée', 'success');
            }
            setShowCategoryForm(false);
            setEditingCategory(null);
            setCategoryLabel('');
            fetchData();
        } catch (error) {
            Swal.fire('Erreur', error.response?.data?.message || 'Impossible d\'enregistrer la catégorie', 'error');
        }
    };

    const handleDeleteCategory = async (id) => {
        const result = await Swal.fire({
            title: 'Supprimer cette catégorie ?',
            text: 'Impossible si des produits y sont affectés.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler'
        });
        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`${API_URL}/categories/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Supprimé', 'Catégorie supprimée avec succès.', 'success');
                fetchData();
            } catch (error) {
                Swal.fire('Erreur', error.response?.data?.message || 'Impossible de supprimer la catégorie', 'error');
            }
        }
    };

    const handleDeleteType = async (id) => {
        const result = await Swal.fire({
            title: 'Supprimer ce type ?',
            text: "Les produits associés pourraient être affectés.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Oui, supprimer'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`${API_URL}/types-produit/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Supprimé', '', 'success');
                fetchData();
            } catch (error) {
                Swal.fire('Erreur', 'Impossible de supprimer le type', 'error');
            }
        }
    };

    // ── Mouvement inventaire
    const openMouvementModal = (produit) => {
        setMouvementProduit(produit);
        setMouvementForm({ typeMouvement: 'retour_client', quantite: '', motif: '', clientId: '' });
        setShowMouvementModal(true);
    };

    const handleMouvementSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            
            // Si ce n'est pas un retour client, on s'assure de ne pas envoyer de clientId
            const payload = { ...mouvementForm };
            if (payload.typeMouvement !== 'retour_client') {
                delete payload.clientId;
            }

            await axios.post(`${API_URL}/mouvements-inventaire/produits/${mouvementProduit._id}/mouvement-special`, payload, { headers });
            
            Swal.fire({
                title: 'Succès',
                text: 'Mouvement enregistré avec succès',
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            setShowMouvementModal(false);
            setMouvementProduit(null);
            fetchData();
        } catch (err) {
            Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors de l\'enregistrement', 'error');
        }
    };

    // Filtrage et Tri des produits
    const filteredProduits = useMemo(() => {
        if (!Array.isArray(produits)) return [];
        let result = produits.filter(produit => {
            const searchStr = searchTerm.toLowerCase();
            const matchesSearch = !searchTerm ||
                produit.nomProduit?.toLowerCase().includes(searchStr) ||
                produit.description?.toLowerCase().includes(searchStr);

            const matchesFournisseur = !filterFournisseur || (fournisseurs.find(f => f._id === filterFournisseur)?.typeProduit?.some(tp => (tp._id || tp) === (produit.typeProduit?._id || produit.typeProduit)));

            return matchesSearch && matchesFournisseur;
        });

        // Appliquer le tri
        const sortKey = sortConfig.key;
        const direction = sortConfig.direction === 'asc' ? 1 : -1;

        result.sort((a, b) => {
            let valA, valB;
            switch (sortKey) {
                case 'nomProduit':
                    valA = a.nomProduit?.toLowerCase() || '';
                    valB = b.nomProduit?.toLowerCase() || '';
                    break;
                case 'quantite':
                    valA = a.quantite || 0;
                    valB = b.quantite || 0;
                    break;
                case 'prix':
                    valA = a.prix || 0;
                    valB = b.prix || 0;
                    break;
                case 'prixAchat':
                    valA = a.prixAchat || 0;
                    valB = b.prixAchat || 0;
                    break;
                case 'TVA':
                    valA = a.TVA || 19;
                    valB = b.TVA || 19;
                    break;
                case 'tauxGain':
                    valA = a.tauxGain || 0;
                    valB = b.tauxGain || 0;
                    break;
                default: return 0;
            }
            if (valA < valB) return -1 * direction;
            if (valA > valB) return 1 * direction;
            return 0;
        });

        return result;
    }, [produits, searchTerm, filterFournisseur, sortConfig, fournisseurs]);

    // ── Helpers prix Triangle ─────────────────────────────────────────────────
    const getPrixVenteTriangle = (prod) => {
        if (!prod) return 0;
        const base = prod.modeGestionStock === 'lineaire' ? (prod.prixMetre || 0) : (prod.prixAchat || 0);
        return base + base * (prod.tauxGain || 0) / 100;
    };
    const fmtTri = (n) => Number(n || 0).toLocaleString('fr-TN', { minimumFractionDigits: 3 });
    const getClientLabel = (client) => {
        if (!client) return '';
        const fullName = [client.nom, client.prenom].filter(Boolean).join(' ').trim();
        return fullName || client.raisonSociale || client.email || 'Client';
    };

    // ── CRUD Triangles ────────────────────────────────────────────────────────
    const openCreateTriangle = () => {
        setEditingTriangle(null);
        setTriangleForm({ nom: '', description: '', categorie: categories[0]?._id || '', statut: 'actif', produits: [], imageFile: null });
        setShowTriangleForm(true);
    };

    const openEditTriangle = (tri) => {
        setEditingTriangle(tri);

        const groupedProduitsMap = new Map();
        (tri.produits || []).forEach(p => {
            const pid = p.produitId?._id || p.produitId;
            if (!groupedProduitsMap.has(pid)) {
                groupedProduitsMap.set(pid, {
                    produitId: pid,
                    quantiteRecommandee: 0,
                    decoupesLineaires: []
                });
            }
            const existing = groupedProduitsMap.get(pid);
            if (p.decoupeLineaire && p.decoupeLineaire.longueurPieceMetre) {
                existing.decoupesLineaires.push(p.decoupeLineaire);
                existing.quantiteRecommandee += (p.decoupeLineaire.metres || 0);
            } else {
                existing.quantiteRecommandee += (p.quantiteRecommandee || 0);
            }
        });

        // Pour les produits linéaires qui n'ont pas de découpe (anciens), on génère une découpe par défaut
        const formProduits = Array.from(groupedProduitsMap.values()).map(p => {
            if (p.decoupesLineaires.length === 0 && produits.find(prod => prod._id === p.produitId)?.modeGestionStock === 'lineaire') {
                p.decoupesLineaires = [{ nombrePieces: 1, longueurPieceMetre: p.quantiteRecommandee, metres: p.quantiteRecommandee }];
            }
            return p;
        });

        setTriangleForm({
            nom: tri.nom,
            description: tri.description || '',
            categorie: tri.categorie?._id || tri.categorie || '',
            statut: tri.statut,
            produits: formProduits,
            imageFile: null
        });
        setShowTriangleForm(true);
    };

    const handleSubmitTriangle = async (e) => {
        e.preventDefault();
        if (!triangleForm.nom || !triangleForm.categorie) {
            Swal.fire('Champs manquants', 'Le nom et la catégorie sont requis.', 'warning');
            return;
        }
        if (triangleForm.produits.length === 0) {
            Swal.fire('Sans produits', 'Ajoutez au moins un produit au triangle.', 'warning');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const fd = new FormData();
            fd.append('nom', triangleForm.nom);
            fd.append('description', triangleForm.description);
            fd.append('categorie', triangleForm.categorie);
            fd.append('statut', triangleForm.statut);
            
            const flattenedProduits = [];
            triangleForm.produits.forEach(p => {
                if (p.decoupesLineaires && p.decoupesLineaires.length > 0) {
                    p.decoupesLineaires.forEach(d => {
                        flattenedProduits.push({
                            produitId: p.produitId,
                            quantiteRecommandee: d.metres,
                            decoupeLineaire: d
                        });
                    });
                } else {
                    flattenedProduits.push({
                        produitId: p.produitId,
                        quantiteRecommandee: p.quantiteRecommandee
                    });
                }
            });
            fd.append('produits', JSON.stringify(flattenedProduits));
            if (triangleForm.imageFile) fd.append('image', triangleForm.imageFile);

            if (editingTriangle) {
                await axios.put(`${API_URL}/triangles/${editingTriangle._id}`, fd, {
                    headers: { ...headers, 'Content-Type': 'multipart/form-data' }
                });
                Swal.fire('Modifié !', 'Triangle mis à jour.', 'success');
            } else {
                await axios.post(`${API_URL}/triangles`, fd, {
                    headers: { ...headers, 'Content-Type': 'multipart/form-data' }
                });
                Swal.fire('Créé !', 'Triangle créé avec succès.', 'success');
            }
            setShowTriangleForm(false);
            setEditingTriangle(null);
            fetchData();
        } catch (err) {
            Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors de l\'enregistrement.', 'error');
        }
    };

    const handleDeleteTriangle = async (id) => {
        const result = await Swal.fire({
            title: 'Supprimer ce triangle ?',
            text: 'Cette action est irréversible !',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler'
        });
        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`${API_URL}/triangles/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Supprimé !', 'Triangle supprimé.', 'success');
                fetchData();
            } catch (err) {
                Swal.fire('Erreur', err.response?.data?.message || 'Impossible de supprimer.', 'error');
            }
        }
    };

    const toggleTriangleProduit = (produitId) => {
        const prod = produitsPourTriangle.find(p => p._id === produitId);
        const exists = triangleForm.produits.findIndex(p => p.produitId === produitId);
        if (exists > -1) {
            setTriangleForm(prev => ({ ...prev, produits: prev.produits.filter(p => p.produitId !== produitId) }));
        } else {
            const newItem = { produitId, quantiteRecommandee: 1 };
            if (prod && prod.modeGestionStock === 'lineaire') {
                newItem.decoupesLineaires = [{ nombrePieces: 1, longueurPieceMetre: 1, metres: 1 }];
            }
            setTriangleForm(prev => ({ ...prev, produits: [...prev.produits, newItem] }));
        }
    };

    const updateTriangleProduitQty = (produitId, qty) => {
        setTriangleForm(prev => ({
            ...prev,
            produits: prev.produits.map(p => p.produitId === produitId ? { ...p, quantiteRecommandee: qty } : p)
        }));
    };

    const addTriangleProduitDecoupe = (produitId) => {
        setTriangleForm(prev => ({
            ...prev,
            produits: prev.produits.map(p => {
                if (p.produitId !== produitId) return p;
                const newCuts = [...(p.decoupesLineaires || []), { nombrePieces: 1, longueurPieceMetre: 1, metres: 1 }];
                const totalMeters = newCuts.reduce((acc, c) => acc + (c.metres || 0), 0);
                return { ...p, decoupesLineaires: newCuts, quantiteRecommandee: totalMeters };
            })
        }));
    };

    const removeTriangleProduitDecoupe = (produitId, index) => {
        setTriangleForm(prev => ({
            ...prev,
            produits: prev.produits.map(p => {
                if (p.produitId !== produitId) return p;
                const newCuts = [...(p.decoupesLineaires || [])];
                newCuts.splice(index, 1);
                if (newCuts.length === 0) {
                     newCuts.push({ nombrePieces: 1, longueurPieceMetre: 1, metres: 1 });
                }
                const totalMeters = newCuts.reduce((acc, c) => acc + (c.metres || 0), 0);
                return { ...p, decoupesLineaires: newCuts, quantiteRecommandee: totalMeters };
            })
        }));
    };

    const updateTriangleProduitDecoupe = (produitId, index, field, value) => {
        setTriangleForm(prev => ({
            ...prev,
            produits: prev.produits.map(p => {
                if (p.produitId !== produitId) return p;
                const newCuts = [...(p.decoupesLineaires || [])];
                if (!newCuts[index]) newCuts[index] = { nombrePieces: 1, longueurPieceMetre: 1, metres: 1 };
                const decoupe = { ...newCuts[index] };
                decoupe[field] = value;
                
                const np = parseFloat(decoupe.nombrePieces) || 0;
                const lp = parseFloat(decoupe.longueurPieceMetre) || 0;
                decoupe.metres = np * lp;
                
                newCuts[index] = decoupe;
                const totalMeters = newCuts.reduce((acc, c) => acc + (parseFloat(c.metres) || 0), 0);
                return { ...p, decoupesLineaires: newCuts, quantiteRecommandee: totalMeters };
            })
        }));
    };

    // Produits filtrés par catégorie sélectionnée dans le formulaire Triangle
    const produitsPourTriangle = triangleForm.categorie
        ? produits.filter(p => (p.categorie?._id || p.categorie) === triangleForm.categorie)
        : [];

    // Triangles filtrés pour l'affichage
    const filteredTriangles = triangles.filter(tri => {
        const matchCat = !triangleFilterCat || (tri.categorie?._id || tri.categorie) === triangleFilterCat;
        const matchSearch = !triangleSearch || tri.nom.toLowerCase().includes(triangleSearch.toLowerCase());
        return matchCat && matchSearch;
    });

    const handleInstantCategoryChange = async (produitId, newCategoryId) => {
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            
            await axios.put(`${API_URL}/produits/${produitId}`, {
                categorie: newCategoryId || null
            }, { headers });
            
            setProduits(prevProduits => prevProduits.map(prod => {
                if (prod._id === produitId) {
                    const matchedCat = categories.find(c => c._id === newCategoryId);
                    return { ...prod, categorie: matchedCat || null };
                }
                return prod;
            }));
            
            Swal.fire({
                title: 'Mis à jour',
                text: 'Catégorie modifiée avec succès.',
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2000
            });
        } catch (error) {
            console.error('Erreur handleInstantCategoryChange:', error);
            Swal.fire('Erreur', 'Impossible de modifier la catégorie', 'error');
        }
    };

    if (viewingProduitId) {
        return (
            <ProduitDetail
                onNavigate={onNavigate}
                onLogout={onLogout}
                currentPage={currentPage}
                produitId={viewingProduitId}
                onBack={() => setViewingProduitId(null)}
            />
        );
    }

    if (viewingBc) {
        return (
            <BonCommandeCreate 
                onNavigate={onNavigate}
                onLogout={onLogout}
                currentPage={currentPage}
                produits={produits}
                types={types}
                fournisseurs={fournisseurs}
                prefill={bcPrefill}
                onBack={() => { setViewingBc(false); setBcPrefill(null); fetchData(); }}
            />
        );
    }

    const handleExportExcel = () => {
        const exportData = filteredProduits.map(p => {
            const stockCounters = getStockCounters(p);
            return {
                'ID PRODUIT': p._id || '',
                'NOM PRODUIT': p.nomProduit || '',
                'RÉFÉRENCE': p.reference || '',
                'DESCRIPTION': p.description || '',
                'ID MARQUE': p.marque?._id || (typeof p.marque === 'string' ? p.marque : ''),
                'MARQUE': p.marque?.name || p.marque || '',
                'ID TYPE PRODUIT': p.typeProduit?._id || (typeof p.typeProduit === 'string' ? p.typeProduit : ''),
                'TYPE PRODUIT': p.typeProduit?.label || p.typeProduit || '',
                'ID CATÉGORIE': p.categorie?._id || (typeof p.categorie === 'string' ? p.categorie : ''),
                'CATÉGORIE': p.categorie?.label || p.categorie || '',
                'MODE GESTION STOCK': p.modeGestionStock || '',
                'QUANTITÉ TOTALE (PHYSIQUE)': p.quantite || 0,
                'QUANTITÉ DISPONIBLE': stockCounters.disponible,
                'QUANTITÉ RÉSERVÉE': stockCounters.reserve,
                'QUANTITÉ VENDUE': stockCounters.vendu,
                'QUANTITÉ DÉFECTUEUSE': p.quantiteDefectueuse || 0,
                'QUANTITÉ RETOURNÉE': p.quantiteRetournee || 0,
                'QUANTITÉ PERDUE': p.quantitePerdue || 0,
                'AFFICHAGE STOCK': formatQuantiteAffichage(p),
                'PRIX ACHAT (TND)': p.prixAchat || 0,
                'PRIX UNITÉ (TND)': p.prix || 0,
                'PRIX MÈTRE (TND)': p.prixMetre || 0,
                'TAUX GAIN (%)': p.tauxGain || 0,
                'TVA (%)': p.TVA || 19,
                'FICHE TECHNIQUE': hasValidDataSheet(p.dataSheet) ? 'Oui' : 'Non',
                'DATE DE CRÉATION': p.createdAt ? new Date(p.createdAt).toLocaleDateString('fr-FR') : ''
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Produits');
        XLSX.writeFile(workbook, 'Produits.xlsx');
    };

    return (
        <div className="pm-layout">
            <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
            <div className="pm-content">
                <div className="pm-header">
                    <div className="pm-header-text">
                        <h1>📦 Gestion des Produits</h1>
                        <p>Administration du catalogue et des stocks produits</p>
                    </div>
                    {hasPermission('produits', 'create') && (
                        <button className="pm-btn-add-hero" onClick={() => { setShowProduitForm(true); setEditingProduit(null); setProduitForm({ ...emptyProduitForm }); }}>
                            <i className="fas fa-plus"></i> Ajouter un produit
                        </button>
                    )}
                </div>
                {loading && <LoadingSpinner message="Chargement du catalogue..." />}
                <div className="pm-main" style={{ display: loading ? 'none' : 'block' }}>

                    <div className="pm-tabs">
                        <button className={`pm-tab ${activeTab === 'produits' ? 'active' : ''}`} onClick={() => setActiveTab('produits')}>
                            Produits ({produits.length})
                        </button>
                        <button className={`pm-tab ${activeTab === 'categories' ? 'active' : ''}`} onClick={() => setActiveTab('categories')}>
                            Catégories ({categories.length})
                        </button>
                        <button className={`pm-tab ${activeTab === 'types' ? 'active' : ''}`} onClick={() => setActiveTab('types')}>
                            Types ({types.length})
                        </button>
                        <button className={`pm-tab pm-tab-triangle ${activeTab === 'triangles' ? 'active' : ''}`} onClick={() => setActiveTab('triangles')}>
                            △ Triangles ({triangles.length})
                        </button>
                    </div>

                    {activeTab === 'produits' && (
                        <div className="pm-tab-content">
                            <div className="pm-search-filter-bar">
                                <div className="pm-search-box">
                                    <input type="text" placeholder="🔍 Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pm-search-input" />
                                </div>
                                <div className="pm-filter-controls">
                                    <select value={filterFournisseur} onChange={(e) => setFilterFournisseur(e.target.value)} className="pm-filter-select">
                                        <option value="">Tous les fournisseurs</option>
                                        {fournisseurs.map(f => <option key={f._id} value={f._id}>{f.raisonSociale}</option>)}
                                    </select>
                                    {(searchTerm || filterFournisseur) && (
                                        <button className="pm-btn-clear-filters" onClick={() => { setSearchTerm(''); setFilterFournisseur(''); }}>✖️ Réinitialiser</button>
                                    )}
                                </div>
                            </div>

                            <div className="pm-actions-bar">
                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                    <button className="pm-btn-add" style={{backgroundColor: '#10b981'}} onClick={handleExportExcel}>
                                        <i className="fas fa-file-excel"></i> Exporter Excel
                                    </button>
                                    <button className="pm-btn-add" style={{ backgroundColor: '#3b82f6' }} onClick={() => onNavigate('demandes-prix')}>
                                        <i className="fas fa-file-invoice-dollar"></i> Demandes de Prix
                                    </button>
                                    {hasPermission('produits', 'create') && (
                                        <>
                                            <button className="pm-btn-add" onClick={() => { setShowProduitForm(true); setEditingProduit(null); setProduitForm({ ...emptyProduitForm }); }}>
                                                ➕ Ajouter un produit
                                            </button>
                                            <button className="pm-btn-submit" style={{ background: '#1e3a5f', border: 'none' }} onClick={() => setViewingBc(true)}>
                                                <i className="fas fa-file-invoice"></i> Créer Bon de Commande
                                            </button>
                                        </>
                                    )}
                                </div>
                                <div className="pm-view-toggle">
                                    <button className={`pm-btn-view-mode ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>📋 Liste</button>
                                    <button className={`pm-btn-view-mode ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>🔲 Grille</button>
                                </div>
                            </div>

                            {showProduitForm && (
                                <div className="pm-form-card" ref={produitFormRef}>
                                    <h3>{editingProduit ? 'Modifier' : 'Nouveau'}</h3>
                                    <form onSubmit={handleSubmitProduit}>
                                        <div className="pm-form-group">
                                            <label>Nom *</label>
                                            <input type="text" name="nomProduit" value={produitForm.nomProduit} onChange={handleProduitInputChange} required />
                                        </div>
                                        <div className="pm-form-group">
                                            <label>Référence</label>
                                            <input type="text" name="reference" value={produitForm.reference} onChange={handleProduitInputChange} placeholder="Ex: REF-001" />
                                        </div>
                                        <div className="pm-form-row">
                                            <div className="pm-form-group"><label>Marque *</label>
                                                <select name="marque" value={produitForm.marque} onChange={handleProduitInputChange} required>
                                                    <option value="">Sélectionner...</option>
                                                    {partenaires.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                                </select>
                                            </div>
                                            <div className="pm-form-group"><label>Type *</label>
                                                <select name="typeProduit" value={produitForm.typeProduit} onChange={handleProduitInputChange} required>
                                                    <option value="">Sélectionner...</option>
                                                    {types.map(t => <option key={t._id} value={t._id}>{t.label}</option>)}
                                                </select>
                                            </div>
                                            <div className="pm-form-group"><label>Catégorie (Désignation)</label>
                                                <select name="categorie" value={produitForm.categorie} onChange={handleProduitInputChange}>
                                                    <option value="">Non classé</option>
                                                    {categories.map(c => <option key={c._id} value={c._id}>{c.label}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="pm-form-row">
                                            <div className="pm-form-group">
                                                <label>Mode de stock</label>
                                                <select name="modeGestionStock" value={produitForm.modeGestionStock} onChange={handleProduitInputChange}>
                                                    <option value="unite">Unité (pièce)</option>
                                                    <option value="lineaire">Linéaire (mètre)</option>
                                                </select>
                                            </div>
                                            <div className="pm-form-group">
                                                <label>{produitForm.modeGestionStock === 'lineaire' ? 'Stock (m)' : 'Quantité'}</label>
                                                <input type="number" name="quantite" value={produitForm.quantite} onChange={handleProduitInputChange} min="0" step={produitForm.modeGestionStock === 'lineaire' ? '0.001' : '1'} />
                                            </div>
                                        </div>
                                        <div className="pm-form-row">
                                            <div className="pm-form-group"><label>Prix d'achat</label><input type="number" name="prixAchat" value={produitForm.prixAchat} onChange={handleProduitInputChange} min="0" step="0.001" /></div>
                                            <div className="pm-form-group">
                                                <label>Prix au mètre (optionnel)</label>
                                                <input
                                                    type="number"
                                                    name="prixMetre"
                                                    value={produitForm.prixMetre}
                                                    onChange={handleProduitInputChange}
                                                    min="0"
                                                    step="0.001"
                                                    disabled={produitForm.modeGestionStock !== 'lineaire'}
                                                />
                                            </div>
                                        </div>
                                        <div className="pm-form-row">
                                            <div className="pm-form-group"><label>Prix de vente public</label><input type="number" name="prix" value={produitForm.prix} onChange={handleProduitInputChange} min="0" step="0.001" /></div>
                                            <div className="pm-form-group"><label>TVA (%)</label><input type="number" name="TVA" value={produitForm.TVA} onChange={handleProduitInputChange} min="0" step="0.001" /></div>
                                        </div>
                                        <div className="pm-form-row">
                                            <div className="pm-form-group"><label>Taux de gain (%)</label><input type="number" name="tauxGain" value={produitForm.tauxGain} onChange={handleProduitInputChange} min="0" step="0.001" /></div>
                                        </div>
                                        <div className="pm-form-group"><label>Description</label><textarea name="description" value={produitForm.description} onChange={handleProduitInputChange} rows="3" required /></div>
                                        <div className="pm-form-group"><label>Image {!editingProduit && '*'}</label><input type="file" accept="image/jpeg, image/jpg, image/png, image/gif, image/webp" onChange={handleImageChange} required={!editingProduit} /></div>
                                        <div className="pm-form-group">
                                            <label>Fiche technique (PDF)</label>
                                            <input type="file" accept="application/pdf" onChange={handleDataSheetChange} />
                                            {editingDataSheetPath && (
                                                <div className="pm-datasheet-current">
                                                    <small>
                                                        Fiche actuelle: <a href={`${API_URL.replace('/api', '')}${editingDataSheetPath}`} target="_blank" rel="noreferrer">Voir le PDF</a>
                                                    </small>
                                                    {hasPermission('produits', 'delete') && (
                                                        <button
                                                            type="button"
                                                            className="pm-btn-icon pm-btn-icon-danger pm-btn-datasheet-remove"
                                                            onClick={handleDeleteCurrentDataSheet}
                                                            title="Supprimer la fiche technique"
                                                            disabled={deletingDataSheet}
                                                        >
                                                            <i className={`fas ${deletingDataSheet ? 'fa-spinner fa-spin' : 'fa-trash-alt'}`}></i>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="pm-form-actions">
                                            <button type="submit" className="pm-btn-submit">{editingProduit ? 'Sauver' : 'Ajouter'}</button>
                                            <button type="button" className="pm-btn-cancel" onClick={() => setShowProduitForm(false)}>Annuler</button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            {viewMode === 'list' && (
                                <>
                                    <div className="pm-results-info">{filteredProduits.length} produit(s)</div>
                                    <div className="pm-list-wrap">
                                        <table className="pm-table">
                                            <thead>
                                                <tr>
                                                    <th>Image</th>
                                                    <th onClick={() => requestSort('nomProduit')} className="pm-th-sortable">Nom {sortConfig.key === 'nomProduit' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : '↕️'}</th>
                                                    <th>Mode</th>
                                                    <th onClick={() => requestSort('quantite')} className="pm-th-sortable">Qté {sortConfig.key === 'quantite' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : '↕️'}</th>
                                                    <th>réservé</th>
                                                    <th>Vendu</th>
                                                    <th>Prix/m</th>
                                                    <th onClick={() => requestSort('prixAchat')} className="pm-th-sortable">P.Achat {sortConfig.key === 'prixAchat' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : '↕️'}</th>
                                                    <th onClick={() => requestSort('TVA')} className="pm-th-sortable">TVA {sortConfig.key === 'TVA' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : '↕️'}</th>
                                                    <th onClick={() => requestSort('tauxGain')} className="pm-th-sortable">Gain {sortConfig.key === 'tauxGain' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : '↕️'}</th>
                                                    <th onClick={() => requestSort('prix')} className="pm-th-sortable">P.Vente {sortConfig.key === 'prix' ? (sortConfig.direction === 'asc' ? '🔼' : '🔽') : '↕️'}</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredProduits.map(produit => (
                                                    <tr key={produit._id}>
                                                        <td data-label="Image" className="pm-td-image"><div className="pm-list-img-wrap"><img src={`${API_URL.replace('/api', '')}${produit.imageProduit}`} alt="" /></div></td>
                                                        <td data-label="Nom"><strong>{produit.nomProduit}</strong></td>
                                                        <td data-label="Mode"><span className="pm-badge-tag">{produit.modeGestionStock === 'lineaire' ? 'Linéaire' : 'Unité'}</span></td>
                                                        <td data-label="Qté"><span className="pm-qty-badge">{formatQuantiteAffichage(produit)}</span></td>
                                                        <td data-label="réservé"><span className="pm-stock-pill reserved">{getStockCounters(produit).reserve}</span></td>
                                                        <td data-label="Vendu"><span className="pm-stock-pill sold">{getStockCounters(produit).vendu}</span></td>
                                                        <td data-label="Prix/m"><strong>{(produit.prixMetre || 0).toLocaleString()} TND</strong></td>
                                                        <td data-label="P.Achat"><strong>{produit.prixAchat?.toLocaleString()} TND</strong></td>
                                                        <td data-label="TVA">{produit.TVA || 19}%</td>
                                                        <td data-label="Gain">{produit.tauxGain || 0}%</td>
                                                        <td data-label="P.Vente"><strong>{produit.prix?.toLocaleString()} TND</strong></td>
                                                        <td data-label="Actions" className="pm-td-actions">
                                                            <div className="pm-list-actions">
                                                                <button className="pm-btn-icon" onClick={() => setViewingProduitId(produit._id)} title="Détails">👁️</button>
                                                                {hasPermission('produits', 'create') && <button className="pm-btn-icon" onClick={() => handleDuplicateProduit(produit)} title="Dupliquer">📄</button>}
                                                                {hasPermission('produits', 'update') && <button className="pm-btn-icon" onClick={() => handleEditProduit(produit)} title="Modifier">✏️</button>}
                                                                {hasPermission('produits', 'delete') && <button className="pm-btn-icon pm-btn-icon-danger" onClick={() => handleDeleteProduit(produit._id)} title="Supprimer">🗑️</button>}
                                                                {hasPermission('produits', 'update') && <button className="pm-btn-icon" style={{color: '#f59e0b'}} onClick={() => openMouvementModal(produit)} title="Déclarer Incident (Retour, Défaut, Perte)">⚠️</button>}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                            {viewMode === 'grid' && (
                                <div className="pm-grid">
                                    {filteredProduits.map(produit => (
                                        <div key={produit._id} className="pm-card">
                                            <div className="pm-card-img"><img src={`${API_URL.replace('/api', '')}${produit.imageProduit}`} alt="" /></div>
                                            <div className="pm-card-body">
                                                <h3 className="pm-card-name">{produit.nomProduit}</h3>
                                                <div className="pm-card-meta">
                                                    <div className="pm-card-meta-item"><span className="pm-card-meta-label">Prix</span><span className="pm-card-meta-value">{produit.prix}</span></div>
                                                    <div className="pm-card-meta-item"><span className="pm-card-meta-label">Mode</span><span className="pm-card-meta-value">{produit.modeGestionStock === 'lineaire' ? 'Linéaire' : 'Unité'}</span></div>
                                                    <div className="pm-card-meta-item"><span className="pm-card-meta-label">Stock</span><span className="pm-card-meta-value">{formatQuantiteAffichage(produit)}</span></div>
                                                    <div className="pm-card-meta-item"><span className="pm-card-meta-label">réservé</span><span className="pm-card-meta-value">{getStockCounters(produit).reserve}</span></div>
                                                    <div className="pm-card-meta-item"><span className="pm-card-meta-label">Vendu</span><span className="pm-card-meta-value">{getStockCounters(produit).vendu}</span></div>
                                                    {produit.modeGestionStock === 'lineaire' && <div className="pm-card-meta-item"><span className="pm-card-meta-label">Prix/m</span><span className="pm-card-meta-value">{produit.prixMetre || 0}</span></div>}
                                                </div>
                                                <div className="pm-card-actions">
                                                    <button className="pm-card-btn pm-card-btn-view" onClick={() => setViewingProduitId(produit._id)}>Détails</button>
                                                    {hasPermission('produits', 'create') && <button className="pm-card-btn pm-card-btn-duplicate" onClick={() => handleDuplicateProduit(produit)}>Dupliquer</button>}
                                                    {hasPermission('produits', 'update') && <button className="pm-card-btn pm-card-btn-edit" onClick={() => handleEditProduit(produit)}>Modifier</button>}
                                                    {hasPermission('produits', 'update') && <button className="pm-card-btn pm-card-btn-edit" style={{backgroundColor: '#f59e0b', borderColor: '#f59e0b', color: '#fff'}} onClick={() => openMouvementModal(produit)}>Incident</button>}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'categories' && (
                        <div className="pm-tab-content">
                            <div className="pm-cat-header-bar">
                                <div className="pm-cat-header-title">
                                    <span className="pm-cat-header-icon">🏷️</span>
                                    <div>
                                        <h3>Gestion des Catégories</h3>
                                        <p>Classez vos produits par catégorie (désignation)</p>
                                    </div>
                                </div>
                                {hasPermission('produits', 'create') && (
                                    <button
                                        className="pm-btn-cat-add"
                                        onClick={() => { setShowCategoryForm(true); setEditingCategory(null); setCategoryLabel(''); }}
                                    >
                                        ➕ Ajouter une catégorie
                                    </button>
                                )}
                            </div>

                            {showCategoryForm && (
                                <div className="pm-cat-form-card">
                                    <h4>{editingCategory ? '✏️ Modifier la catégorie' : '➕ Nouvelle catégorie'}</h4>
                                    <form onSubmit={handleSubmitCategory} className="pm-cat-form">
                                        <div className="pm-form-group">
                                            <label>Nom de la catégorie (Désignation)</label>
                                            <input
                                                type="text"
                                                value={categoryLabel}
                                                onChange={(e) => setCategoryLabel(e.target.value)}
                                                placeholder="Ex: Module photovoltaïque Luxor, Onduleur..."
                                                required
                                                autoFocus
                                            />
                                        </div>
                                        <div className="pm-form-actions">
                                            <button type="submit" className="pm-btn-submit">
                                                <i className="fas fa-save"></i> {editingCategory ? 'Mettre à jour' : 'Ajouter'}
                                            </button>
                                            <button
                                                type="button"
                                                className="pm-btn-cancel"
                                                onClick={() => { setShowCategoryForm(false); setEditingCategory(null); setCategoryLabel(''); }}
                                            >
                                                Annuler
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            <div className="pm-categories-board" style={{ overflowX: 'auto' }}>
                                {[...categories, { _id: 'unclassified', label: 'Non classés' }].map(cat => {
                                    const catProducts = produits.filter(prod => {
                                        if (cat._id === 'unclassified') {
                                            return !prod.categorie;
                                        }
                                        return (prod.categorie?._id || prod.categorie) === cat._id;
                                    });

                                    return (
                                        <div key={cat._id} className="pm-category-column">
                                            <div className="pm-category-column-header">
                                                <div className="pm-cat-col-title">
                                                    <h3 title={cat.label}>{cat.label}</h3>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexShrink: 0 }}>
                                                        <span className="pm-category-count">{catProducts.length}</span>
                                                        {cat._id !== 'unclassified' && hasPermission('produits', 'update') && (
                                                            <>
                                                                <button
                                                                    className="pm-btn-cat-edit"
                                                                    title="Modifier"
                                                                    onClick={() => { setEditingCategory(cat); setCategoryLabel(cat.label); setShowCategoryForm(true); }}
                                                                >✏️</button>
                                                                <button
                                                                    className="pm-btn-cat-delete"
                                                                    title="Supprimer"
                                                                    onClick={() => handleDeleteCategory(cat._id)}
                                                                >🗑️</button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="pm-category-column-body">
                                                {catProducts.length === 0 ? (
                                                    <div className="pm-category-empty">Aucun produit</div>
                                                ) : (
                                                    catProducts.map(prod => (
                                                        <div key={prod._id} className="pm-category-product-item">
                                                            <div className="pm-category-product-img">
                                                                {prod.imageProduit ? (
                                                                    <img src={`${API_URL.replace('/api', '')}${prod.imageProduit}`} alt="" />
                                                                ) : (
                                                                    <div className="pm-category-product-placeholder">📦</div>
                                                                )}
                                                            </div>
                                                            <div className="pm-category-product-details">
                                                                <h4 className="pm-category-product-name" title={prod.nomProduit}>
                                                                    {prod.nomProduit}
                                                                </h4>
                                                                <span className="pm-category-product-price">
                                                                    {(prod.prix || 0).toLocaleString()} TND
                                                                </span>
                                                            </div>
                                                            {hasPermission('produits', 'update') && (
                                                                <div className="pm-category-select-wrapper">
                                                                    <select
                                                                        value={prod.categorie?._id || prod.categorie || ''}
                                                                        onChange={(e) => handleInstantCategoryChange(prod._id, e.target.value)}
                                                                        className="pm-category-select"
                                                                    >
                                                                        <option value="">-- Choisir Catégorie --</option>
                                                                        {categories.map(c => (
                                                                            <option key={c._id} value={c._id}>
                                                                                {c.label}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {activeTab === 'types' && (
                        <div className="pm-tab-content">
                            <div className="pm-actions-bar">
                                {hasPermission('produits', 'create') && (
                                    <button className="pm-btn-add" onClick={() => { setShowTypeForm(true); setEditingType(null); setTypeLabel(''); }}>➕ Ajouter un type</button>
                                )}
                            </div>
                            {showTypeForm && (
                                <div className="pm-form-card pm-type-form-anim">
                                    <form onSubmit={handleSubmitType}>
                                        <div className="pm-form-group">
                                            <label><i className="fas fa-tag"></i> Nom du Type</label>
                                            <input 
                                                type="text" 
                                                value={typeLabel} 
                                                onChange={(e) => setTypeLabel(e.target.value)} 
                                                placeholder="Ex: Batteries solaires, Onduleurs..."
                                                required 
                                            />
                                        </div>
                                        <div className="pm-form-actions">
                                            <button type="submit" className="pm-btn-submit">
                                                <i className="fas fa-save"></i> Enregistrer
                                            </button>
                                            <button 
                                                type="button" 
                                                className="pm-btn-cancel" 
                                                onClick={() => {
                                                    setShowTypeForm(false);
                                                }}
                                            >
                                                Annuler
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}
                            <div className="pm-types-grid">
                                {types.map(t => (
                                    <div key={t._id} className="pm-type-item">
                                        <div className="pm-type-info">
                                            <span className="pm-type-label">{t.label}</span>
                                        </div>
                                        <div className="pm-type-actions">
                                            <button className="pm-btn-type-edit" onClick={() => { setEditingType(t); setTypeLabel(t.label); setShowTypeForm(true); }}>
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button className="pm-btn-type-delete" onClick={() => handleDeleteType(t._id)}>
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {activeTab === 'triangles' && (
                        <div className="pm-tab-content">
                            {/* ── En-tête ── */}
                            <div className="tri-header-bar">
                                <div className="tri-header-info">
                                    <span className="tri-header-icon">△</span>
                                    <div>
                                        <h3>Gestion des Triangles</h3>
                                        <p>Modèles prédéfinis de produits par catégorie pour pré-remplir les Packs</p>
                                    </div>
                                </div>
                                {hasPermission('produits', 'create') && (
                                    <button className="pm-btn-cat-add tri-btn-create" onClick={openCreateTriangle}>
                                        △ Nouveau Triangle
                                    </button>
                                )}
                            </div>

                            {/* ── Filtres ── */}
                            <div className="tri-filters-bar">
                                <input
                                    type="text"
                                    className="tri-search-input"
                                    placeholder="Rechercher un triangle..."
                                    value={triangleSearch}
                                    onChange={e => setTriangleSearch(e.target.value)}
                                />
                                <select
                                    className="tri-cat-filter"
                                    value={triangleFilterCat}
                                    onChange={e => setTriangleFilterCat(e.target.value)}
                                >
                                    <option value="">Toutes les catégories</option>
                                    {categories.map(c => <option key={c._id} value={c._id}>{c.label}</option>)}
                                </select>
                            </div>

                            {/* ── Formulaire création/modification ── */}
                            {showTriangleForm && (
                                <div className="pm-form-card tri-form-card">
                                    <h3>{editingTriangle ? `✏️ Modifier : ${editingTriangle.nom}` : '△ Nouveau Triangle'}</h3>
                                    <form onSubmit={handleSubmitTriangle}>
                                        <div className="pm-form-row">
                                            <div className="pm-form-group">
                                                <label>Nom du Triangle *</label>
                                                <input
                                                    type="text"
                                                    value={triangleForm.nom}
                                                    onChange={e => setTriangleForm({ ...triangleForm, nom: e.target.value })}
                                                    placeholder="Ex: Toiture Tôle, Toiture Terrasse..."
                                                    required
                                                />
                                            </div>
                                            <div className="pm-form-group">
                                                <label>Catégorie *</label>
                                                <select
                                                    value={triangleForm.categorie}
                                                    onChange={e => setTriangleForm({ ...triangleForm, categorie: e.target.value, produits: [] })}
                                                    required
                                                >
                                                    <option value="">Sélectionner...</option>
                                                    {categories.map(c => <option key={c._id} value={c._id}>{c.label}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="pm-form-row">
                                            <div className="pm-form-group">
                                                <label>Description</label>
                                                <textarea
                                                    value={triangleForm.description}
                                                    onChange={e => setTriangleForm({ ...triangleForm, description: e.target.value })}
                                                    placeholder="Description du triangle..."
                                                    rows="2"
                                                />
                                            </div>
                                            <div className="pm-form-group">
                                                <label>Statut</label>
                                                <select
                                                    value={triangleForm.statut}
                                                    onChange={e => setTriangleForm({ ...triangleForm, statut: e.target.value })}
                                                >
                                                    <option value="actif">Actif</option>
                                                    <option value="inactif">Inactif</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="pm-form-group">
                                            <label>Image du Triangle</label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={e => setTriangleForm({ ...triangleForm, imageFile: e.target.files[0] })}
                                            />
                                            {editingTriangle?.image && !triangleForm.imageFile && (
                                                <div className="tri-current-img">
                                                    <img
                                                        src={`${API_URL.replace('/api', '')}${editingTriangle.image}`}
                                                        alt="Image actuelle"
                                                    />
                                                    <small>Image actuelle</small>
                                                </div>
                                            )}
                                        </div>

                                        {/* Sélection des produits */}
                                        <div className="tri-produits-section">
                                            <div className="tri-produits-title">
                                                Produits du Triangle
                                                <span className="tri-produits-count">{triangleForm.produits.length} sélectionné{triangleForm.produits.length > 1 ? 's' : ''}</span>
                                            </div>
                                            {!triangleForm.categorie ? (
                                                <div className="tri-no-cat-hint">Sélectionnez d'abord une catégorie pour voir les produits disponibles.</div>
                                            ) : produitsPourTriangle.length === 0 ? (
                                                <div className="tri-no-cat-hint">Aucun produit dans cette catégorie.</div>
                                            ) : (
                                                <div className="tri-produits-grid">
                                                    {produitsPourTriangle.map(prod => {
                                                        const selected = triangleForm.produits.find(p => p.produitId === prod._id);
                                                        const prixVente = getPrixVenteTriangle(prod);
                                                        const unite = prod.modeGestionStock === 'lineaire' ? 'm' : 'u';
                                                        return (
                                                            <div
                                                                key={prod._id}
                                                                className={`tri-produit-card ${selected ? 'selected' : ''}`}
                                                                onClick={() => toggleTriangleProduit(prod._id)}
                                                            >
                                                                <div className="tri-prod-check">{selected ? '✓' : ''}</div>
                                                                {prod.imageProduit && (
                                                                    <img
                                                                        src={`${API_URL.replace('/api', '')}${prod.imageProduit}`}
                                                                        alt=""
                                                                        className="tri-prod-img"
                                                                    />
                                                                )}
                                                                <div className="tri-prod-info">
                                                                    <span className="tri-prod-name">{prod.nomProduit}</span>
                                                                    <span className="tri-prod-mode">{prod.modeGestionStock === 'lineaire' ? '📏 Linéaire' : 'Unité'}</span>
                                                                    <span className="tri-prod-price">{fmtTri(prixVente)} DT/{unite}</span>
                                                                </div>
                                                                {selected && (
                                                                    <div className="tri-prod-qty" onClick={e => e.stopPropagation()}>
                                                                        {prod.modeGestionStock === 'lineaire' ? (
                                                                            <div className="tri-decoupe-lineaire">
                                                                                {(selected.decoupesLineaires || []).map((cut, cutIndex) => (
                                                                                    <div key={cutIndex} className="tri-decoupe-row" style={{ marginBottom: '8px' }}>
                                                                                        <div className="tri-decoupe-field" style={{ flex: '0.8' }}>
                                                                                            <label>Pièces</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                min="1"
                                                                                                step="1"
                                                                                                value={cut.nombrePieces !== undefined ? cut.nombrePieces : 1}
                                                                                                onChange={e => updateTriangleProduitDecoupe(prod._id, cutIndex, 'nombrePieces', e.target.value)}
                                                                                            />
                                                                                        </div>
                                                                                        <div className="tri-decoupe-field" style={{ flex: '1' }}>
                                                                                            <label>Long. (m)</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                min="0.001"
                                                                                                step="any"
                                                                                                value={cut.longueurPieceMetre !== undefined ? cut.longueurPieceMetre : 1}
                                                                                                onChange={e => updateTriangleProduitDecoupe(prod._id, cutIndex, 'longueurPieceMetre', e.target.value)}
                                                                                            />
                                                                                        </div>
                                                                                        <div className="tri-decoupe-field" style={{ flex: '1' }}>
                                                                                            <label>Total</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                readOnly
                                                                                                value={Number(cut.metres || 0).toFixed(2)}
                                                                                                style={{ background: '#f1f5f9', cursor: 'not-allowed' }}
                                                                                            />
                                                                                        </div>
                                                                                        <button 
                                                                                            type="button" 
                                                                                            className="tri-btn-delete-cut"
                                                                                            onClick={() => removeTriangleProduitDecoupe(prod._id, cutIndex)}
                                                                                            title="Supprimer cette découpe"
                                                                                        >
                                                                                            ×
                                                                                        </button>
                                                                                    </div>
                                                                                ))}
                                                                                <button 
                                                                                    type="button" 
                                                                                    className="tri-btn-add-cut" 
                                                                                    onClick={() => addTriangleProduitDecoupe(prod._id)}
                                                                                >
                                                                                    + Ajouter une découpe
                                                                                </button>
                                                                                <div style={{ marginTop: '5px', fontSize: '0.75rem', fontWeight: 'bold', color: '#6d28d9', textAlign: 'right' }}>
                                                                                    Total produit: {Number(selected.quantiteRecommandee || 0).toFixed(2)} m
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <>
                                                                                <label>Qté ({unite})</label>
                                                                                <input
                                                                                    type="number"
                                                                                    min="0.001"
                                                                                    step="any"
                                                                                    value={selected.quantiteRecommandee}
                                                                                    onChange={e => updateTriangleProduitQty(prod._id, e.target.value)}
                                                                                />
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>

                                        <div className="pm-form-actions">
                                            <button type="submit" className="pm-btn-submit">
                                                {editingTriangle ? '✅ Enregistrer' : '🚀 Créer le Triangle'}
                                            </button>
                                            <button type="button" className="pm-btn-cancel" onClick={() => { setShowTriangleForm(false); setEditingTriangle(null); }}>
                                                Annuler
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            {/* ── Grille des triangles ── */}
                            <div className="tri-cards-grid">
                                {filteredTriangles.map(tri => {
                                    const totalPrix = (tri.produits || []).reduce((sum, item) => {
                                        const prod = item.produitId;
                                        return sum + getPrixVenteTriangle(prod) * (item.quantiteRecommandee || 1);
                                    }, 0);
                                    return (
                                        <div key={tri._id} className="tri-card">
                                            <div className="tri-card-accent" />
                                            {tri.image ? (
                                                <img
                                                    src={`${API_URL.replace('/api', '')}${tri.image}`}
                                                    alt={tri.nom}
                                                    className="tri-card-image"
                                                />
                                            ) : (
                                                <div className="tri-card-no-image">△</div>
                                            )}
                                            <div className="tri-card-body">
                                                <div className="tri-card-top">
                                                    <span className="tri-cat-badge">{tri.categorie?.label || '—'}</span>
                                                    <span className={`tri-status-pill ${tri.statut}`}>{tri.statut === 'actif' ? 'Actif' : 'Inactif'}</span>
                                                </div>
                                                <h3 className="tri-card-name">{tri.nom}</h3>
                                                {tri.description && <p className="tri-card-desc">{tri.description}</p>}
                                                <div className="tri-card-produits">
                                                    <span className="tri-prods-label">{tri.produits?.length || 0} produit{tri.produits?.length !== 1 ? 's' : ''}</span>
                                                    <ul className="tri-prods-list">
                                                        {(tri.produits || []).slice(0, 4).map((item, i) => {
                                                            const prod = item.produitId;
                                                            const isLineaire = prod?.modeGestionStock === 'lineaire';
                                                            const decoupe = item.decoupeLineaire;
                                                            let qtyDisplay;
                                                            if (isLineaire && decoupe && decoupe.longueurPieceMetre) {
                                                                const np = decoupe.nombrePieces || 1;
                                                                const lp = Number(decoupe.longueurPieceMetre).toFixed(2);
                                                                const total = Number(item.quantiteRecommandee).toFixed(2);
                                                                qtyDisplay = np > 1
                                                                    ? `${np}×${lp}m = ${total}m`
                                                                    : `${lp} m`;
                                                            } else if (isLineaire) {
                                                                qtyDisplay = `${Number(item.quantiteRecommandee).toFixed(2)} m`;
                                                            } else {
                                                                qtyDisplay = `×${item.quantiteRecommandee}`;
                                                            }
                                                            return (
                                                                <li key={i}>
                                                                    <span>• {prod?.nomProduit || 'Produit'}{isLineaire ? ' 📏' : ''}</span>
                                                                    <strong>{qtyDisplay}</strong>
                                                                </li>
                                                            );
                                                        })}
                                                        {tri.produits?.length > 4 && (
                                                            <li className="tri-more">+{tri.produits.length - 4} autres...</li>
                                                        )}
                                                    </ul>
                                                </div>
                                                <div className="tri-card-price">
                                                    <span className="tri-price-label">Valeur estimée</span>
                                                    <span className="tri-price-value">{fmtTri(totalPrix)} DT</span>
                                                </div>
                                            </div>
                                            <div className="tri-card-footer">
                                                {hasPermission('produits', 'update') && (
                                                    <button className="tri-btn-edit" onClick={() => openEditTriangle(tri)}>✏️ Modifier</button>
                                                )}
                                                {hasPermission('produits', 'delete') && (
                                                    <button className="tri-btn-delete" onClick={() => handleDeleteTriangle(tri._id)}>🗑️</button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                {filteredTriangles.length === 0 && (
                                    <div className="tri-empty">
                                        <div className="tri-empty-icon">△</div>
                                        <h3>Aucun triangle trouvé</h3>
                                        <p>Créez des triangles pour pré-configurer rapidement vos Packs solaires.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal Mouvement Inventaire */}
            {showMouvementModal && (
                <div className="pm-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div className="pm-modal-content" style={{ background: '#fff', padding: '20px', borderRadius: '8px', width: '400px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ marginTop: 0, color: '#1e293b' }}>Déclarer un incident</h3>
                        <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>Produit: <strong>{mouvementProduit?.nomProduit}</strong></p>
                        
                        <form onSubmit={handleMouvementSubmit}>
                            <div className="pm-form-group" style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Type d'incident</label>
                                <select 
                                    value={mouvementForm.typeMouvement}
                                    onChange={e => {
                                        const nextType = e.target.value;
                                        setMouvementForm(prev => ({
                                            ...prev,
                                            typeMouvement: nextType,
                                            clientId: nextType === 'retour_client' ? prev.clientId : ''
                                        }));
                                    }}
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                                    required
                                >
                                    <option value="perte_casse">Perte / Casse</option>
                                    <option value="defectueux">Produit Défectueux</option>
                                    <option value="retour_client">Retour Client</option>
                                    <option value="reintegration">Réintégration</option>
                                </select>
                            </div>

                            {mouvementForm.typeMouvement === 'retour_client' && (
                                <div className="pm-form-group" style={{ marginBottom: '15px' }}>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Client</label>
                                    <select
                                        value={mouvementForm.clientId}
                                        onChange={e => setMouvementForm({ ...mouvementForm, clientId: e.target.value })}
                                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                                        required
                                    >
                                        <option value="">-- Choisir un client --</option>
                                        {clients.map(client => (
                                            <option key={client._id} value={client._id}>
                                                {getClientLabel(client)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            
                            <div className="pm-form-group" style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Quantité</label>
                                <input 
                                    type="number" 
                                    min="0.001" 
                                    step="any"
                                    value={mouvementForm.quantite}
                                    onChange={e => setMouvementForm({...mouvementForm, quantite: e.target.value})}
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                                    required
                                />
                            </div>

                            <div className="pm-form-group" style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Motif / Explication</label>
                                <textarea 
                                    value={mouvementForm.motif}
                                    onChange={e => setMouvementForm({...mouvementForm, motif: e.target.value})}
                                    rows="3"
                                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                                    placeholder="Expliquez brièvement la raison..."
                                    required
                                />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button type="button" onClick={() => setShowMouvementModal(false)} style={{ padding: '8px 16px', background: '#e2e8f0', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Annuler</button>
                                <button type="submit" style={{ padding: '8px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Confirmer</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProduitsManagement;
